import React, { useState, FC, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import Button from "../../components/ui/Button";
import { StatProps, SectionProps } from "../../types/components";
import Toast from "../../components/ui/Toast";
import {
  JobPostResponse,
  CompanyDetails,
  PostSkillsRequest,
  SkillPriority,
  Currency,
  JobApplicationStatus,
  UserJobApplicationResponse,
} from "../../types/api";
import companyService from "../../services/company.service";
import jobService from "../../services/job.service";
import { mapskills } from "../../utils/jobPostMapper";

const Stat: FC<StatProps> = ({ label, value, icon }) => (
  <div className="rounded-xl bg-white/90 backdrop-blur border border-gray-100 p-4 text-center hover:shadow-lg transition-shadow">
    <div className="flex justify-center space-x-3">
      {icon && <div className="mb-2 flex justify-center h-4">{icon}</div>}
      <div className="text-sm text-gray-500">{label}</div>
    </div>
    <div className="mt-1 text-lg font-semibold text-primary-black">{value}</div>
  </div>
);

const Section: FC<SectionProps> = ({ title, children, className = "" }) => (
  <section
    className={`rounded-2xl bg-white/90 backdrop-blur border border-gray-100 p-6 ${className}`}
  >
    <h3 className="text-xl font-bold text-primary-black mb-4">{title}</h3>
    {children}
  </section>
);

interface SkillBadgeProps {
  skill: string;
  variant?: "green" | "blue";
}

const SkillBadge: FC<SkillBadgeProps> = ({ skill, variant = "green" }) => {
  const baseStyle = "inline-block px-3 py-1 text-sm font-medium rounded-full";
  const variantStyle =
    variant === "green"
      ? "bg-green-200 text-green-900"
      : "bg-teal-100 text-teal-800";
  return <span className={`${baseStyle} ${variantStyle}`}>{skill}</span>;
};

export default function Jobs() {
  const { postId } = useParams<{ postId: string }>();
  const [jobPost, setJobPost] = useState<JobPostResponse | null>(null);
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info" | "warning";
  } | null>(null);
  const [applicationStatus, setApplicationStatus] =
    useState<JobApplicationStatus | null>(null);
  const [applicationId, setApplicationId] = useState<number | null>(null);

  useEffect(() => {
    const fetchJobAndCompanyDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!postId) {
          throw new Error("Job ID is missing.");
        }
        const jobResponse = await jobService.getJobPost(Number(postId));
        if (jobResponse.status === "success" && jobResponse.data) {
          setJobPost(jobResponse.data);

          if (jobResponse.data.companyId) {
            const companyResponse = await companyService.getCompanyDetails(
              jobResponse.data.companyId,
            );
            if (companyResponse.status === "success" && companyResponse.data) {
              setCompanyDetails(companyResponse.data);
            } else {
              console.error(
                "Failed to fetch company details:",
                companyResponse.status,
              );
              setError("Failed to load company information.");
            }
          }

          if ((user && user.role == "Jobseeker") || user?.role == "Intern") {
            const applicationsResponse = await jobService.getUserApplications(
              user.userId,
              1,
              100,
            );
            const application = applicationsResponse.data.items?.find(
              (app) => app.postId === Number(postId),
            );
            if (application) {
              setApplicationStatus(application.status || null);
              setApplicationId(application.applicationId || null);
            }
          }
        } else {
          console.error("Failed to fetch job post:", jobResponse.status);
          setError("Failed to load job details.");
        }
      } catch (err: any) {
        console.error("Error fetching job or company details:", err);
        setError(err.message || "An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    };

    fetchJobAndCompanyDetails();
  }, [postId, user]);

  const handleApplicationStatusChange = async (
    newStatus: JobApplicationStatus,
  ) => {
    if (!user || !jobPost) return;

    try {
      if (applicationId) {
        await jobService.updateJobApplication({
          applicationId: applicationId,
          newStatus: newStatus,
          modifiedBy: user.userId,
        });
      } else {
        const newApplicationId = await jobService.createJobApplication({
          postId: jobPost.postId,
          userId: user.userId,
          createdBy: user.userId,
        });
        await jobService.updateJobApplication({
          applicationId: newApplicationId,
          newStatus: newStatus,
          modifiedBy: user.userId,
        });
        setApplicationId(newApplicationId);
      }
      setApplicationStatus(newStatus);
      setToast({
        message: `Job ${newStatus === "Applied" ? "application submitted" : "saved"} successfully!`,
        type: "success",
      });
    } catch (error) {
      console.error("Error updating application status:", error);
      setToast({ message: "Failed to update job status.", type: "error" });
    }
  };

  const formatSalary = (min?: number, max?: number, currency?: Currency) => {
    if (!min && !max) return "Not disclosed";
    const minStr = min
      ? `${currency === "INR" ? "₹" : ""}${min.toLocaleString()}`
      : "";
    const maxStr = max
      ? `${currency === "INR" ? "₹" : ""}${max.toLocaleString()}`
      : "";
    if (min && max) return `${minStr} – ${maxStr} CTC`;
    if (min) return `${minStr}+ CTC`;
    return `${maxStr} CTC`;
  };

  const formatExperience = (min?: number) => {
    if (min === 0) return "Fresher";
    if (min === 1) return "1 year";
    if (min && min > 1) return `${min} years`;
    return "Not specified";
  };

  const formatPostedDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getSkillNames = (
    skills?: PostSkillsRequest[],
    priority?: SkillPriority,
  ) => {
    if (!skills) return [];
    const filteredSkills = mapskills(skills, priority);
    return filteredSkills.map((s) => s.skillName);
  };

  const handleStartApplication = () => {
    if (!jobPost) return;

    if (!isAuthenticated) {
      navigate("/login", {
        state: {
          from: `/jobs/${jobPost.postId}`,
          message: "Please log in to apply for this job",
        },
      });
      return;
    }

    if (user?.role !== "Jobseeker") {
      setToast({
        message:
          "Only users can apply for jobs. Please switch to a user account.",
        type: "warning",
      });
      return;
    }

    handleApplicationStatusChange("Applied");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading job details...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        {error}
      </div>
    );
  }

  if (!jobPost || !companyDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Job not found or company details unavailable.
      </div>
    );
  }

  const CompanyDetailsSection: React.FC<{ className?: string }> = ({
    className,
  }) => (
    <Section
      className={`${className} sm:col-span-2`}
      title="Company information"
    >
      <div className="grid grid-cols-1 gap-3 mb-4">
        <p className="text-l font-bold text-primary-black">Description</p>
        <div className="text-gray-700 leading-relaxed mb-4 ml-2">
          {companyDetails.description}
        </div>
        <p className="text-l font-bold text-primary-black">Strength</p>
        <div className="text-gray-700 leading-relaxed mb-4 ml-2">
          {companyDetails.empStrength || "N/A"}
        </div>
        <p className="text-l font-bold text-primary-black">Address</p>
        <div className="text-gray-700 leading-relaxed mb-4 ml-2">
          {companyDetails.address || "N/A"}
        </div>
      </div>
      {companyDetails.webpageUrl && (
        <a
          href={companyDetails.webpageUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-block"
        >
          <Button variant="outline" size="sm" className="w-full">
            Visit Website
          </Button>
        </a>
      )}
    </Section>
  );
  const NiceToHaveSection: React.FC<{ className?: string }> = ({
    className,
  }) => (
    <div className={className}>
      {getSkillNames(jobPost.skills, "Preferred").length > 0 && (
        <Section title="Nice to Have Skills">
          <div className="flex flex-wrap gap-2">
            {getSkillNames(jobPost.skills, "Preferred").map((skill, idx) => (
              <SkillBadge key={idx} skill={skill} variant="blue" />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-primary-teaGreen/5 to-white">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          position="top-right"
          duration={2500}
          onClose={() => setToast(null)}
          show
        />
      )}
      <section className="relative overflow-hidden md:pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white backdrop-blur border border-gray-100 p-6 md:p-8 mb-8 shadow-xl">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {companyDetails.attachments?.find(
                (att) => att.typeId === "CompanyLogo",
              )?.fileUrl ? (
                <div
                  className={`h-16 w-16 md:h-20 md:w-20 rounded-2xl flex items-center justify-center font-extrabold text-primary-black text-xl md:text-2xl overflow-hidden`}
                >
                  <img
                    src={
                      companyDetails.attachments.find(
                        (att) => att.typeId === "CompanyLogo",
                      )?.fileUrl
                    }
                    alt={`${companyDetails.companyName} logo`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div
                  className={`h-16 w-16 md:h-20 md:w-20 bg-gray-200 rounded-2xl flex items-center justify-center font-extrabold text-primary-black text-xl md:text-2xl overflow-hidden`}
                >
                  {companyDetails.companyName
                    ? companyDetails.companyName[0]
                    : "N/A"}
                </div>
              )}

              <div className="flex-1">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-primary-black mb-2">
                      {jobPost.jobTitle}
                    </h1>
                    <div className="text-gray-600 text-lg mb-4 lg:mb-0">
                      {companyDetails.companyName} •{" "}
                      {jobPost.locationIds?.join(", ") || "Remote"} •{" "}
                      {jobPost.jobType}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      size="lg"
                      className="px-8 py-3 text-lg font-semibold"
                      onClick={handleStartApplication}
                      disabled={
                        ![
                          "Invited",
                          "Bookmarked",
                          "ViewedByCandidate",
                        ].includes(applicationStatus || "") ||
                        user?.role !== "Jobseeker"
                      }
                    >
                      {!["Invited", "Bookmarked", "ViewedByCandidate"].includes(
                        applicationStatus || "",
                      )
                        ? "Applied"
                        : "Apply Now"}
                    </Button>
                    <Button
                      variant={
                        applicationStatus === "Bookmarked"
                          ? "primary"
                          : "outline"
                      }
                      size="lg"
                      className="px-8 py-3 text-lg"
                      onClick={() =>
                        handleApplicationStatusChange("Bookmarked")
                      }
                      disabled={
                        !["Invited", "ViewedByCandidate"].includes(
                          applicationStatus || "",
                        ) || user?.role !== "Jobseeker"
                      }
                    >
                      {!["Invited", "ViewedByCandidate"].includes(
                        applicationStatus || "",
                      )
                        ? "Saved"
                        : "Save Job"}
                    </Button>
                    {(user?.role == "Agent" || user?.role == "Admin") && (
                      <Link to= {`/${user?.role.toLowerCase()}/post/${postId}/applications`}>
                        <Button
                          variant={
                            applicationStatus === "Bookmarked"
                              ? "primary"
                              : "outline"
                          }
                          size="lg"
                          className="px-8 py-3 text-lg"
                          // onClick={() => handleApplicationStatusChange("Bookmarked")}
                          disabled={
                            user?.role !== "Agent" && user?.role !== "Admin"
                          }
                        >
                          {"View Applications"}
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <Stat
                    label="Salary"
                    value={formatSalary(
                      jobPost.minSalary,
                      jobPost.maxSalary,
                      jobPost.salaryCurrency,
                    )}
                  />
                  <Stat
                    label="Experience"
                    value={formatExperience(jobPost.minExperience)}
                    icon={
                      <svg
                        className="h-5 w-5 text-primary-lightGreen"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6"
                        />
                      </svg>
                    }
                  />
                  <Stat
                    label="Posted"
                    value={formatPostedDate(jobPost.createdOn)}
                    icon={
                      <svg
                        className="h-5 w-5 text-primary-lightGreen"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    }
                  />
                  <Stat
                    label="Industry"
                    value={companyDetails.industry || "N/A"}
                    icon={
                      <svg
                        className="h-5 w-5 text-primary-lightGreen"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="sm:col-span-2 space-y-6">
              <Section title="About this role">
                <p className="text-gray-700 leading-relaxed text-lg">
                  {jobPost.jobDescription}
                </p>
              </Section>
              <CompanyDetailsSection className="hidden lg:block" />
            </div>
            <div className="space-y-6 ">
              {getSkillNames(jobPost.skills, "Required").length > 0 && (
                <Section title="Required Skills">
                  <div className="flex flex-wrap gap-2">
                    {getSkillNames(jobPost.skills, "Required").map(
                      (skill, idx) => (
                        <SkillBadge key={idx} skill={skill} variant="green" />
                      ),
                    )}
                  </div>
                </Section>
              )}
              <NiceToHaveSection className="hidden lg:block" />
            </div>
            <NiceToHaveSection className="block space-y-6 lg:hidden" />

            <div className="space-y-6 sm:col-span-2">
              <CompanyDetailsSection className="block lg:hidden " />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
